<template>
    <div
        v-if="isLoggedIn"
        class="topbar items-center grid bg-base-100 top-bar border-base-200 border-solid border-b-2 h-[54px]"
        :class="selectedOrg ? 'top-bar-grid' : 'top-bar-grid-simple'"
    >
        <img
            class="cursor-pointer px-2 2xl:hidden h-8"
            :src="isDark ? '/c-logo-dark.svg' : '/c-logo.svg'"
            alt="Conta logo"
            title="Startside"
            :data-cy="TopBarCy.Logo"
            @click="setHome"
        >
        <img
            class="cursor-pointer px-2 hidden 2xl:block"
            :src="isDark ? '/conta-logo-dark.svg' : '/conta-logo-light.svg'"
            alt="Conta logo"
            :data-cy="TopBarCy.Logo"
            @click="setHome"
        >

        <org-selector class="cursor-pointer" :data-cy="TopBarCy.OrgSelector"/>

        <div v-if="selectedOrg" class="justify-center items-center gap-4 flex w-fit mx-auto">
            <button
                class="menu-item item underline-hover-animation"
                title="Bilagsmottak"
                @click="setupIncomingTasksWithTransactions"
            >
                <i class="i-mdi-file-document-multiple block"/>
                <span class="item-text">Bilagsmottak</span>
            </button>
            <targeting-router-link
                :to="CREATE_TRANSACTION"
                class="menu-item item underline-hover-animation"
                :data-cy="TopBarCy.TransactionManager"
            >
                <div class="menu-item" title="Nytt bilag">
                    <i class="i-mdi-file-document-plus"/>
                    <span class="item-text">Nytt bilag</span>
                </div>
            </targeting-router-link>
            <targeting-router-link
                :to="INVOICE_CREATOR"
                class="menu-item item underline-hover-animation"
                :data-cy="TopBarCy.InvoiceCreator"
            >
                <div class="menu-item" title="Ny faktura">
                    <i class="i-mdi-format-list-group-plus block"/>
                    <span class="item-text">Ny faktura</span>
                </div>
            </targeting-router-link>

            <dropdown-menu ref="reportsMenuRef" top-bar>
                <template #button>
                    <div
                        class="menu-item item underline-hover-animation cursor-pointer"
                        :data-cy="TopBarCy.Reports"
                        title="Rapporter"
                    >
                        <i class="i-ph-chart-bar-fill"/>
                        <span class="item-text">Rapporter</span>
                    </div>
                </template>
                <template #dropdown="{close}">
                    <reports-menu :close="close" class="p-2"/>
                </template>
            </dropdown-menu>

            <targeting-router-link
                :to="CUSTOMERS"
                class="menu-item item underline-hover-animation"
                :data-cy="TopBarCy.Customers"
            >
                <div class="menu-item" title="Kunder">
                    <div class="indicator">
                        <span v-if="customers.length != 0" class="indicator-item z-0 hover:text-accent">{{ customers.length }}</span>
                        <i class="i-ph-users-three-fill"/>
                    </div>
                    <span class="item-text">Kunder</span>
                </div>
            </targeting-router-link>
            <targeting-router-link
                :to="SUPPLIERS"
                class="menu-item item underline-hover-animation"
                :data-cy="TopBarCy.Suppliers"
            >
                <div class="menu-item" title="Leverandører">
                    <div class="indicator">
                        <span v-if="suppliers.length != 0" class="indicator-item z-0">{{ suppliers.length }}</span>
                        <i class="i-ph-truck-fill"/>
                    </div>
                    <span class="item-text">Leverandører</span>
                </div>
            </targeting-router-link>
            <top-bar-layouts-drop-down/>
        </div>


        <div
            class="menu-item item underline-hover-animation pb-0.5 cursor-pointer flex justify-center pr-0.5"
            title="Søk"
            @click="() => emitter.emit('SHOW_COMMAND_BAR')"
        >
            <div class="i-mdi-magnify text-2xl"/>
        </div>


        <panel-count-selector/>

        <div v-if="selectedOrg" class="flex items-center gap-1">
            <help-popup/>
            <theme-switcher class="menu-item"/>
            <announcement-popup-button v-if="false"/>
        </div>


        <div v-if="selectedOrg" class="w-fit flex gap-1 mr-2">
            <dropdown-menu ref="settingsMenuRef" top-bar direction="left">
                <template #button>
                    <button class="menu-item flex items-center" title="Instillinger" data-cy="topbar-settings-menu">
                        <i class="text-lg i-ph-gear"/>
                    </button>
                </template>
                <template #dropdown="{close}">
                    <settings-menu :close class="p-2"/>
                </template>
            </dropdown-menu>
        </div>
    </div>
</template>

<script lang="ts" setup>
// TODO: This styling is pretty hacky and janky. When UX decides on a new design, replace the whole thing with NuxtUI
import {ref, watch} from 'vue'
import {selectedOrg} from '@/tenants/org.service'
import DropdownMenu from '@/components/DropdownMenu.vue'
import { TargetingRouterLink } from '@tritt/multipanel'
import HelpPopup from '@/help/HelpPopup.vue'
import OrgSelector from '@/tenants/OrgSelector.vue'
import ThemeSwitcher from '@/theme/ThemeSwitcher.vue'
import {isDark} from '@/theme/theme.service'
import {isLoggedIn} from '@/services/infrastructure/auth.service'
import {INVOICE_CREATOR} from '@/invoice'
import {CUSTOMERS, SUPPLIERS} from '@/subaccount'
import {CREATE_TRANSACTION} from '@/transaction/create-transaction'
import ReportsMenu from '@/components/reports/ReportsMenu.vue'
import SettingsMenu from '@/components/settings/SettingsMenu.vue'
import TopBarLayoutsDropDown from '@/favourites/TopBarLayoutsDropDown.vue'
import PanelCountSelector from '@/multipanel-glue/PanelCountSelector.vue'
import {usePanelManager} from '@tritt/multipanel'
import {TogglableDropdown} from '@/types/TogglableDropdown'
import hotkeys from 'hotkeys-js'
import {useRoute} from 'vue-router'
import {useSubAccounts} from '@/subaccount/subAccount.service'
import {DASHBOARD} from '@/dashboard'
import {TASKS} from '@/task-manager'
import emitter from '@/services/infrastructure/emitter'
import AnnouncementPopupButton from '@/announcements/AnnouncementPopupButton.vue'
import {TopBarCy} from '@/components/TopBarCy'

const route = useRoute()
const {customers, suppliers} = useSubAccounts()

const {setPanelPath, panelDragSource, panelCount} = usePanelManager()
const setupIncomingTasksWithTransactions = async () => {
    await setPanelPath({componentName: TASKS.componentName, panelId: 1})
    await setPanelPath({componentName: CREATE_TRANSACTION.componentName, panelId: 2})
}

const setHome = async () => {
    await setPanelPath({componentName: DASHBOARD.componentName, panelId: 1})
    panelCount.value = 1
}

const reportsMenuRef = ref<TogglableDropdown | null>(null)
const settingsMenuRef = ref<TogglableDropdown | null>(null)

hotkeys('esc', () => {
    reportsMenuRef.value?.closeDropdown()
    settingsMenuRef.value?.closeDropdown()
})

watch(route, () => {
    if (!panelDragSource.value) {
        reportsMenuRef.value?.closeDropdown()
        settingsMenuRef.value?.closeDropdown()
    }
})

</script>

<style scoped>
@reference "@/index.css";
@reference "tailwindcss";

.menu-item {
    @apply items-center mx-0.5 text-xs py-1 flex flex-col;

    i {
        @apply text-lg mr-1;
    }
}

.item {
    @apply flex flex-col;
}

.item-text {
    display: none;
    @media (min-width: 1250px) {
        display: block;
    }
}

.top-bar-grid {
    grid-template-columns: minmax(50px, auto) 250px minmax(400px, 4150px) auto auto 90px 26px;

    @media (min-width: 1250px) {
        grid-template-columns: auto 250px minmax(400px, 4180px) auto auto 90px 26px;
    }
}

.top-bar-grid-simple {
    grid-template-columns: minmax(48px, auto) minmax(200px, auto) minmax(0, 10000px) auto;
}

.indicator-item {
    @apply bg-base-100 rounded-sm;
}


</style>